import React, { useCallback, useEffect, useState, useRef } from "react";
import SuperScoreRound from "./SuperScoreRound";
import GameOver from "./GameOver";
import ParticleExplosion from "./Components/ParticleExplosion"; // 👈 Add Import
import { motion } from "framer-motion";
import { closestCenter, KeyboardSensor, MeasuringStrategy, pointerWithin } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay

} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";

import { auth } from "./firebase/firebase"; // 👈 Add Import
import { getChronoscorePointsForGuessCount } from "./gameSession";

const isTestingSuperMode = false; // 👈 flip to false for normal mode

const shuffle = (array) => {
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const sortableCollisionDetection = (args) => {
  const pointerIntersections = pointerWithin(args);
  return pointerIntersections.length > 0 ? pointerIntersections : closestCenter(args);
};

const SortableItem = React.memo(({
  item,
  index,
  itemElsRef,
  measuredHeightsRef,
  draggedId,
  perfectScore,
  animateScrolls,
  flippedIndex,
  formatYear,
  selectedIndex,
  showCorrect,
  readyForChronoscore,
  onClick,
  isCompactLayout,
  // isInstantSwap,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useSortable({
    id: item.id,
    animateLayoutChanges: () => true,
    transition: null, // Stop dnd-kit from using CSS transitions
  });

  const isAnyDrag = draggedId != null;

  // ref: store DOM el for FLIP + measure height once
  const refCallback = (el) => {
    setNodeRef(el);
    if (!el) return;
    if (itemElsRef && itemElsRef.current) {
      itemElsRef.current[item.id] = el;
    }
    if (measuredHeightsRef && measuredHeightsRef.current && !measuredHeightsRef.current[item.id]) {
      measuredHeightsRef.current[item.id] = el.getBoundingClientRect().height;
    }
  };

  const springConfig = { type: "spring", stiffness: 500, damping: 40, mass: 0.8 };

  const style = {
    willChange: "transform",
    backfaceVisibility: "hidden",
    cursor: perfectScore ? "pointer" : "grab",
    animation: animateScrolls
      ? `scroll-pop 0.4s ease ${index * 100}ms forwards`
      : undefined,
    zIndex: isDragging ? 999 : 1,
    opacity: isDragging ? 0 : 1,
    height:
      isAnyDrag && measuredHeightsRef && measuredHeightsRef.current[item.id]
        ? `${measuredHeightsRef.current[item.id]}px`
        : undefined,
  };

  return (
    <motion.div
      // layout={!isInstantSwap}
      layout
      className="scroll-wrapper"
      ref={refCallback}
      style={style}
      animate={{
        x: transform?.x || 0,
        y: transform?.y || 0,
      }}
      transition={{
        // layout: isInstantSwap ? { duration: 0 } : springConfig,
        layout: springConfig,
        x: springConfig,
        y: springConfig,
      }}
      {...attributes}
      {...listeners}
    >
      <div
        className={`scroll-card ${selectedIndex === index && !perfectScore ? "selected" : ""
          }`}
        style={{ fontSize: isCompactLayout ? "1rem" : "1.2rem", pointerEvents: "auto" }}
        onClick={(e) => onClick(e, index)}
      >
        {(perfectScore ||
          (showCorrect && !readyForChronoscore)) &&
          flippedIndex === index
          ? formatYear(item.year)
          : item.event}
      </div>
    </motion.div>
  );
});

const Chronologicle = ({ displayName, onScoreUpdate, equippedEmblem, equippedTitle, currentDay, setCurrentDay, isPremiumMode, initialEvents, scoreFinalized,
  setScoreFinalized, setActivePanel, setShowLocker, onPlayUnlimited }) => {

  useEffect(() => {
    swapSound.current = new Audio("/sounds/swapp.mp3");
    swapSound.current.preload = "auto";
  }, []);

  // 💾 Persistence Helper
  const getStorageKey = useCallback(() => {
    const uid = auth.currentUser?.uid || localStorage.getItem("guestUid");
    if (!uid || !currentDay) return null;
    return `chrono_state_${currentDay}_${uid}`;
  }, [currentDay]);


  // 💾 Sync Init Helper
  const getSavedValue = (key, defaultValue) => {
    const storageKey = getStorageKey();
    if (!storageKey) return defaultValue;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed[key] !== undefined) return parsed[key];
      } catch (e) { }
    }
    return defaultValue;
  };

  const [lives, setLives] = useState(() => getSavedValue("lives", 5));
  const [gameOver, setGameOver] = useState(() => getSavedValue("gameOver", false));
  const [showCorrect, setShowCorrect] = useState(false);
  const [retryMode, setRetryMode] = useState(false);

  const [eventSet, setEventSet] = useState(() => getSavedValue("eventSet", []));
  const [playerOrder, setPlayerOrder] = useState(() => getSavedValue("playerOrder", []));
  const [feedback, setFeedback] = useState("");
  const [perfectScore, setPerfectScore] = useState(() => getSavedValue("perfectScore", false));
  const [flippedIndex, setFlippedIndex] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [numGuesses, setNumGuesses] = useState(() => getSavedValue("numGuesses", 0));
  const [showSuperPrompt, setShowSuperPrompt] = useState(() => getSavedValue("showSuperPrompt", false));
  const [wantsSuperScore, setWantsSuperScore] = useState(() => getSavedValue("wantsSuperScore", null));
  const [readyForChronoscore, setReadyForChronoscore] = useState(() => getSavedValue("readyForChronoscore", false));
  const [shakeScrolls, setShakeScrolls] = useState(false);
  const [animateScrolls, setAnimateScrolls] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [chronoscore, setChronoscore] = useState(() => getSavedValue("chronoscore", 0));
  const [displayedScore, setDisplayedScore] = useState(0);
  const prevScoreRef = useRef(0);
  const animationRef = useRef();
  const [superScoreTotal, setSuperScoreTotal] = useState(() => getSavedValue("superScoreTotal", 0));
  const [isInSuperRound, setIsInSuperRound] = useState(() => getSavedValue("isInSuperRound", false));
  const [lockOrder, setLockOrder] = useState(false);
  const [, setShowPointsPopup] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(() => getSavedValue("pointsEarned", 0));
  const [flashPhase, setFlashPhase] = useState(1); // 0 = points, 1 = congrats (starts with congrats)
  const [activeItem, setActiveItem] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  // 👉 add this one here (must be before DndContext and onDragStart)
  const measuredHeightsRef = useRef({}); // { [id]: number }
  const itemElsRef = useRef({});   // id -> DOM node for each .scroll-wrapper
  // const [isInstantSwap, setIsInstantSwap] = useState(false);

  // 💾 Persist Effect (Save on critical changes)
  useEffect(() => {
    if (!currentDay || playerOrder.length === 0) return;

    const key = getStorageKey();
    if (!key) return;

    // Only save if game is active (or just finished)
    // We don't want to save empty states over good ones.
    const state = {
      lives,
      numGuesses,
      playerOrder,
      eventSet,
      gameOver,
      readyForChronoscore,
      perfectScore,
      chronoscore,
      pointsEarned,
      showSuperPrompt,
      wantsSuperScore,
      isInSuperRound,
      superScoreTotal,
      currentDay // verify match on load
    };

    localStorage.setItem(key, JSON.stringify(state));
  }, [lives, numGuesses, playerOrder, eventSet, gameOver, readyForChronoscore, perfectScore, chronoscore, pointsEarned, showSuperPrompt, wantsSuperScore, isInSuperRound, superScoreTotal, currentDay, getStorageKey]);


  // 💾 Load Effect (Restore on mount)
  useEffect(() => {
    if (!currentDay || !initialEvents || initialEvents.length === 0) return;

    const key = getStorageKey();
    if (!key) return;

    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.currentDay === currentDay) {
          console.log("🔄 Restoring saved game state...");

          // Restore Core State
          setLives(parsed.lives);
          setNumGuesses(parsed.numGuesses);
          setPlayerOrder(parsed.playerOrder);
          setEventSet(parsed.eventSet); // Important: Use the saved set, not initialEvents (though they should match for daily)
          setGameOver(parsed.gameOver);
          setReadyForChronoscore(parsed.readyForChronoscore);
          setPerfectScore(parsed.perfectScore);
          setChronoscore(
            parsed.chronoscore
            ?? parsed.pointsEarned
            ?? getChronoscorePointsForGuessCount(parsed.numGuesses)
          );
          setPointsEarned(
            parsed.pointsEarned
            ?? parsed.chronoscore
            ?? getChronoscorePointsForGuessCount(parsed.numGuesses)
          );
          setShowSuperPrompt(parsed.showSuperPrompt);
          setWantsSuperScore(parsed.wantsSuperScore);
          setIsInSuperRound(parsed.isInSuperRound);
          setSuperScoreTotal(parsed.superScoreTotal);

          // If game was over or won, ensure locks are set
          if (parsed.gameOver || parsed.readyForChronoscore || parsed.perfectScore) {
            setLockOrder(true);
          }

          return; // Skip default init
        }
      } catch (err) {
        console.warn("Failed to restore game state:", err);
      }
    }

    // Default Init (only if no save loaded)
    setLives(5);
    setGameOver(false);
    setNumGuesses(0);
    setEventSet(initialEvents);
    setPlayerOrder(shuffle(initialEvents));
    setFeedback("");
    setPerfectScore(false);
    setFlippedIndex(null);
    setSelectedIndex(null);
    setShowCorrect(false);
    setRetryMode(false);
    setReadyForChronoscore(false);
    setChronoscore(0);
    setLockOrder(false);
    setPointsEarned(0);
    setShowSuperPrompt(false);
    setWantsSuperScore(null);
    setIsInSuperRound(false);
    setSuperScoreTotal(0);

    if (isTestingSuperMode) {
      setWantsSuperScore(true);
      setIsInSuperRound(true);
    }
  }, [initialEvents, currentDay, getStorageKey]); // Depend on initialEvents to know when to start




  useEffect(() => {
    swapSound.current = new Audio("/sounds/swapp.mp3");
  }, []);


  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getPointsColor = (points) => {
    if (points === 40) return "#FFD700"; // Gold
    if (points === 30) return "#3bfd7a"; // Pink
    if (points === 20) return "#3bfd7a"; // Blue
    if (points === 10) return "#3bfd7a"; // Green
    return "#e63d3d";                   // Grey / 0
  };


  const swapSound = useRef(null);
  const dragPreventClickRef = useRef(false);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 600 || window.innerHeight < 700);
  const isCompactLayout = window.innerWidth < 360 || window.innerHeight < 700;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600 || window.innerHeight < 700);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const formatYear = (year) => {
    return year < 0 ? `${Math.abs(year)} BCE` : `${year}`;
  };

  useEffect(() => {
    const totalScore = chronoscore + superScoreTotal;
    if (totalScore !== prevScoreRef.current) {
      const duration = isInSuperRound ? 1400 : 2000; // ✅ dynamic duration
      const start = performance.now();
      const startScore = prevScoreRef.current;
      const endScore = totalScore;

      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

      const step = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const value = Math.floor(startScore + (endScore - startScore) * easeOutCubic(progress));
        setDisplayedScore(value);
        if (progress < 1) {
          animationRef.current = requestAnimationFrame(step);
        } else {
          prevScoreRef.current = endScore;
        }
      };

      cancelAnimationFrame(animationRef.current);
      animationRef.current = requestAnimationFrame(step);
    }
  }, [chronoscore, superScoreTotal, isInSuperRound]);

  // ✅ Add this right after
  useEffect(() => {
    onScoreUpdate(displayedScore);
  }, [displayedScore, onScoreUpdate]);

  // 🔄 Flash phase toggle for points/congrats alternation
  // 🔄 Flash phase toggle for points/congrats alternation
  useEffect(() => {
    if ((readyForChronoscore || perfectScore) && pointsEarned > 0) {
      const interval = setInterval(() => {
        setFlashPhase((prev) => (prev === 0 ? 1 : 0));
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setFlashPhase(1); // Reset to 1 so it starts with Congratulations
    }
  }, [readyForChronoscore, perfectScore, pointsEarned]);


  // 🗑️ Original Init Effect Removed (Handled by Load Effect above)

  const unlockAudio = () => {
    if (swapSound.current) {
      swapSound.current.play().catch(() => { }); // silent fail
      swapSound.current.pause();
      swapSound.current.currentTime = 0;
    }
  };

  const checkOrder = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    // Always play guess sound immediately
    const guessSound = new Audio("/sounds/guesss.mp3");
    guessSound.play().catch(() => { });

    const currentGuessCount = numGuesses + 1;
    setNumGuesses(currentGuessCount);
    const correctOrder = [...eventSet].sort((a, b) => a.year - b.year);
    let score = 0;

    // Calculate score for each guess based on the number of guesses
    for (let i = 0; i < playerOrder.length; i++) {
      if (playerOrder[i].event === correctOrder[i].event) score++;
    }

    // Calculate points based on guess number (starting from 40)
    const points = getChronoscorePointsForGuessCount(currentGuessCount);

    // ✅ Only trigger scroll pop animation if they got everything right (score 6)
    if (score === 6) {
      setAnimateScrolls(true);
      setTimeout(() => setAnimateScrolls(false), 1000);

      setTimeout(() => {
        const successSound = new Audio("/sounds/successsfx.mp3");
        successSound.play().catch(() => { });

        setLockOrder(true); // Lock movement no matter what
        setFeedback(`${score}/6`);
        setFlippedIndex(null);
        setSelectedIndex(null);
        setIsSubmitting(false);
        setPointsEarned(points); // 🎯 set how many points they earned
        setShowPointsPopup(true);    // ⭐️ pop it up

        setTimeout(() => {            // 🕒 auto-hide the popup after 2.5 seconds
          setShowPointsPopup(false);
        }, 2500);

        setChronoscore(points); // 🔥 always set points, even if it's +30, +20, +10, or 0

        if (currentGuessCount === 1) {
          // Perfect on first guess ➔ Chronoscore opportunity
          setReadyForChronoscore(true);

        } else if (lives <= 1) {
          // NOT perfect, and almost or actually out of lives ➔ allow flipping
          setPerfectScore(true);
        } else {
          // Still not ready for flip or chronoscore yet, player keeps going
          setReadyForChronoscore(true); // still lets them "continue" to super score if they manage
        }
      }, 800);
    } else {
      // ❌ WRONG: Fail IMMEDIATELY with shake (no vertical pop, no 800ms delay)
      const failSound = new Audio("/sounds/failagainnn.mp3");
      failSound.play().catch(() => { });

      setFeedback(`${score}/6`);

      if (!retryMode) {
        setLives(prev => {
          if (prev <= 1) {
            // 🛑 INFINITE GUESSES: Stay at 0 lives, no reveal.
            return 0;
          } else {
            return prev - 1;
          }
        });
      }

      setShakeScrolls(true);
      setTimeout(() => setShakeScrolls(false), 400); // 400ms duration for shake
      setTimeout(() => setIsSubmitting(false), 600); // unlock after shake
    }
  };

  return (
    <div
      onClick={unlockAudio}
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >

      <div className="game-layout">



        {/* 🧩 SUPER SCORE PROMPT — Fullscreen and centered */}
        {showSuperPrompt && wantsSuperScore === null && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100vh",
              textAlign: "center",
              padding: "2rem",
            }}
          >
            <h2 style={{ fontSize: "3rem", marginBottom: "1rem" }}>
              100% Correct!
            </h2>
            <h2 style={{ fontSize: "2rem", marginBottom: "1rem" }}>
              Go for your Chronoscore?
            </h2>
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                className="submit-button"
                onClick={() => {
                  setWantsSuperScore(true);
                  setShowSuperPrompt(false);
                }}
              >
                Yes
              </button>
              <button
                className="submit-button"
                onClick={() => {
                  setWantsSuperScore(false);
                  setShowSuperPrompt(false);
                  setPerfectScore(true); // Unlock original flip view
                }}
              >
                No
              </button>
            </div>
          </div>
        )}

        {wantsSuperScore === true && (
          <SuperScoreRound
            playerOrder={playerOrder}
            chronoscore={chronoscore}
            onScoreUpdate={(runningTotal) => setSuperScoreTotal(runningTotal)}
            onScoreFinalized={(final) => {
              console.log("Final score:", final);
              setScoreFinalized(true); // ✅ lock score on finalize
            }}
            isSuperScore={true}
            displayName={displayName}
            currentDay={currentDay}
            isPremiumMode={isPremiumMode}
            setActivePanel={setActivePanel}
            setScoreFinalized={setScoreFinalized}
            setShowLocker={setShowLocker}
            onPlayUnlimited={onPlayUnlimited} // ✅ Pass handler
            numGuesses={numGuesses} // ✅ Pass total guesses
          />
        )}

        {/* 🎮 MAIN GAME UI (only show if NOT in Super Score phase) */}
        {!(showSuperPrompt || wantsSuperScore) && (
          <>



            <div className="title-block">
              {(readyForChronoscore || perfectScore) && <ParticleExplosion />}

              <h1
                className="game-title"
                style={isCompactLayout ? { fontSize: "1.65rem", lineHeight: 1.05 } : undefined}
              >
                {(readyForChronoscore || perfectScore) ? (
                  flashPhase === 0 ? (
                    <motion.span
                      key="points"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      style={{ color: getPointsColor(pointsEarned) }}
                    >
                      {pointsEarned === 0 ? "0 Points" : `+${pointsEarned} Points`}
                    </motion.span>
                  ) : (
                    <motion.span
                      key="congrats"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      Congratulations!
                    </motion.span>
                  )
                ) : (
                  `Chronologicle #${currentDay}`
                )}
              </h1>
              <p
                className="game-subtitle"
                style={isCompactLayout ? { fontSize: "0.95rem", marginTop: "0.35rem", maxWidth: "calc(100vw - 1.5rem)" } : undefined}
              >
                {readyForChronoscore || perfectScore
                  ? "Click continue to go for your Chronoscore."
                  : "Drag the events to order from oldest ↑ to most recent ↓"}
              </p>

            </div>

            <div className="outer-padding">

              {/* Points popup moved to header */}

              <DndContext
                sensors={sensors}
                collisionDetection={sortableCollisionDetection}
                measuring={{
                  droppable: { strategy: MeasuringStrategy.Always },
                  draggable: { strategy: MeasuringStrategy.Always },

                }}
                modifiers={[restrictToVerticalAxis]}
                autoScroll={false}   // ← stop the edge auto-scroll nudge
                onDragStart={({ active }) => {
                  // snapshot every row so neighbors never reflow
                  playerOrder.forEach(e => {
                    const el = itemElsRef.current?.[e.id];
                    if (el) measuredHeightsRef.current[e.id] = el.getBoundingClientRect().height;
                  });
                  // block taps, clear selection, mark which is dragged
                  dragPreventClickRef.current = true;
                  setSelectedIndex(null);
                  setDraggedId(active.id);

                  // seed the overlay + spacer height so first frame isn't 0
                  const h = active.rect?.current?.initial?.height;
                  if (h && !measuredHeightsRef.current[active.id]) {
                    measuredHeightsRef.current[active.id] = h;
                  }

                  // set the active item for your overlay content
                  const found = playerOrder.find((e) => e.id === active.id);
                  setActiveItem(found);
                }}


                onDragOver={({ active, over }) => {
                  if (!over || active.id === over.id) return;
                  if (lockOrder) return;

                  // --- EDGE GUARDS (top/bottom) ---
                  const activeRect = active.rect.current.translated ?? active.rect.current.initial;
                  if (activeRect) {
                    const firstId = playerOrder[0]?.id;
                    const lastId = playerOrder[playerOrder.length - 1]?.id;
                    const firstEl = firstId ? itemElsRef.current[firstId] : null;
                    const lastEl = lastId ? itemElsRef.current[lastId] : null;

                    if (firstEl && lastEl) {
                      const midY = activeRect.top + activeRect.height / 2;
                      const firstTop = firstEl.getBoundingClientRect().top;
                      const lastBottom = lastEl.getBoundingClientRect().bottom;
                      const EDGE_PAD = 6; // small hysteresis so it doesn’t flicker

                      const oldIndex = playerOrder.findIndex(e => e.id === active.id);

                      // to top
                      if (midY < firstTop + EDGE_PAD && oldIndex > 0) {
                        setPlayerOrder(items => arrayMove(items, oldIndex, 0));
                        return;
                      }
                      // to bottom
                      if (midY > lastBottom - EDGE_PAD && oldIndex < playerOrder.length - 1) {
                        setPlayerOrder(items => arrayMove(items, oldIndex, items.length - 1));
                        return;
                      }
                    }
                  }
                  // --- NORMAL HOVER SWAP ---
                  const oldIndex = playerOrder.findIndex((e) => e.id === active.id);
                  const newIndex = playerOrder.findIndex((e) => e.id === over.id);
                  if (oldIndex !== newIndex) {
                    setPlayerOrder((items) => arrayMove(items, oldIndex, newIndex));
                  }
                }}
                onDragEnd={({ active, over }) => {
                  // we already reordered during onDragOver; just cleanup + optional sound
                  if (swapSound.current && over && active.id !== over.id) {
                    swapSound.current.currentTime = 0;
                    swapSound.current.play().catch(() => { });
                  }
                  setTimeout(() => {
                    dragPreventClickRef.current = false;
                    setDraggedId(null);
                    setActiveItem(null);
                  }, 50);
                }}
                onDragCancel={() => {
                  // same cleanup if the drag is canceled
                  setTimeout(() => {
                    dragPreventClickRef.current = false;
                    setDraggedId(null);
                    setActiveItem(null);
                  }, 50);
                }}
              >
                <SortableContext
                  items={playerOrder.map((e) => e.id)}
                  strategy={verticalListSortingStrategy} // 👈 smooth neighbor transforms
                >
                  <div className={shakeScrolls ? "scroll-shake" : ""}>
                    {playerOrder.map((item, index) => (
                      <SortableItem
                        key={item.id}
                        item={item}
                        index={index}
                        itemElsRef={itemElsRef}
                        measuredHeightsRef={measuredHeightsRef}
                        draggedId={draggedId}
                        perfectScore={perfectScore}
                        animateScrolls={animateScrolls}
                        flippedIndex={flippedIndex}
                        formatYear={formatYear}
                        selectedIndex={selectedIndex}
                        showCorrect={showCorrect}
                        readyForChronoscore={readyForChronoscore}
                        isCompactLayout={isCompactLayout}
                        onClick={(e, idx) => {
                          e.stopPropagation();
                          if (dragPreventClickRef.current) return;

                          if (lockOrder) {
                            if (swapSound.current) {
                              swapSound.current.currentTime = 0;
                              swapSound.current.play().catch(() => { });
                            }
                            return;
                          }

                          // if (selectedIndex === null) {
                          //   setSelectedIndex(idx);
                          //   if (swapSound.current) {
                          //     swapSound.current.currentTime = 0;
                          //     swapSound.current.play().catch(() => { });
                          //   }
                          // } else if (selectedIndex === idx) {
                          //   setSelectedIndex(null);
                          // }
                          else {
                            // setIsInstantSwap(true);
                            // const newOrder = [...playerOrder];
                            // [newOrder[selectedIndex], newOrder[idx]] = [newOrder[idx], newOrder[selectedIndex]];
                            // setPlayerOrder(newOrder);
                            // setSelectedIndex(null);

                            // // Snap back to "normal" in the next frame so drags are still smooth
                            // setTimeout(() => {
                            //   setIsInstantSwap(false);
                            // }, 0);

                            // if (swapSound.current) {
                            //   swapSound.current.currentTime = 0;
                            //   swapSound.current.play().catch(() => { });
                            // }
                          }
                        }}
                      // isInstantSwap={isInstantSwap}
                      />
                    ))}
                  </div>
                </SortableContext>
                <DragOverlay
                  dropAnimation={null}
                  adjustScale={false}
                  fadeIn={false}
                  // 👇 add this style to smooth the active item
                  style={{ willChange: "transform", backfaceVisibility: "hidden" }}
                >
                  {activeItem && (
                    <div
                      className="scroll-wrapper overlay-wrapper"
                      style={{
                        width: "100%",
                        zIndex: 9999,
                        margin: 0,
                        position: "relative",
                        overflow: "visible",
                      }}
                    >
                      <div
                        className="scroll-card overlay-card"
                        style={{ fontSize: isCompactLayout ? "1rem" : "1.2rem", overflow: "visible" }}
                      >
                        {activeItem.event}
                      </div>
                    </div>
                  )}
                </DragOverlay>
              </DndContext>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0",
                maxWidth: "700px", // Match scroll full width including ends
                width: "100%",
                margin: "0 auto 0", // centers horizontally
                padding: isCompactLayout ? "0 0.5rem" : "0 .75rem",
                boxSizing: "border-box"
              }}
            >
              {/* Row 1: Guesses + Score */}
              <div
                style={{
                  display: "flex",
                  gap: isCompactLayout ? "0.5rem" : "0.75rem",
                  width: "100%",
                }}
              >

                {/* Guesses */}
                <div
                  style={{
                    flex: 1,
                    height: isCompactLayout ? "46px" : "52px",
                    backgroundColor: "#00201a",
                    color: "#e7c17f",
                    borderRadius: "8px",
                    fontFamily: "'Baskerville', 'Garamond', 'Georgia', 'serif'",
                    fontSize: isCompactLayout ? "1rem" : "1.1rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center", // center the inner wrapper
                    boxShadow: "none",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  {/* Inner wrapper handles left vs center */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: isMobile ? "center" : "flex-start",
                      width: isMobile ? "100%" : "auto", // for clean centering on mobile
                    }}
                  >
                    {!isMobile && (
                      <div
                        style={{
                          marginRight: "1.2rem",
                          fontWeight: "bold",
                          fontSize: isCompactLayout ? "1rem" : "1.2rem",
                        }}
                      >
                        Guesses:
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: isMobile ? "1rem" : "1rem", // 👈 spacing between dots
                        width: "100%",
                      }}
                    >
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span
                          key={i}
                          style={{
                            opacity: i < lives ? 1 : 0.2,
                            fontSize: isCompactLayout ? "1.3rem" : "1.5rem",
                          }}
                        >
                          ●
                        </span>
                      ))}
                    </div>

                  </div>
                </div>

                {/* Score */}
                <div
                  style={{
                    flex: 1,
                    height: isCompactLayout ? "46px" : "52px",
                    backgroundColor: "#00201a",
                    color: "#e7c17f", // Always normal gold color
                    borderRadius: "8px",
                    fontFamily: "'Baskerville', 'Garamond', 'Georgia', 'serif'",
                    fontWeight: "bold",
                    fontSize: isCompactLayout ? "1.3rem" : "1.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "none",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",

                  }}
                >
                  {readyForChronoscore ? "6/6" : feedback || " /6"}
                </div>

              </div>

              {/* Submit Button */}
              <button
                className="submit-button"
                style={{
                  border: "none",
                  height: isCompactLayout ? "3.25rem" : "3.75rem",
                  fontWeight: "bold",
                  fontSize: isCompactLayout ? "1.25rem" : "1.5rem",
                  width: "100%",
                  padding: "0",
                  borderRadius: "8px",
                  boxShadow: "none",

                  background: "linear-gradient(135deg, #002d21, #00372a)",

                  color: "#e7c17f",
                }}
                onClick={
                  readyForChronoscore || perfectScore
                    ? () => {
                      setWantsSuperScore(true);
                      setReadyForChronoscore(false);
                      setIsInSuperRound(true);
                      setShowSuperPrompt(false);
                    }
                    : checkOrder
                }
                disabled={isSubmitting}
              >
                {readyForChronoscore || perfectScore ? "Continue" : "Submit"}
              </button>
            </div>




            {/* Game Over screen (stays unchanged) */}
            {gameOver && (
              <GameOver
                onRetry={() => {
                  setPerfectScore(false);
                  setFlippedIndex(null);
                  setSelectedIndex(null);
                  setLives(Infinity);
                  setGameOver(false);
                  setShowCorrect(false);
                  setRetryMode(true);
                }}
                onShowCorrect={() => {
                  const correctOrder = [...eventSet].sort((a, b) => a.year - b.year);
                  setPlayerOrder([...correctOrder]);
                  setShowCorrect(true);
                  setGameOver(false);
                  setPerfectScore(true);
                }}
              />
            )}
          </>
        )}

      </div>
    </div>
  );

};

export default Chronologicle;
